import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { type Registry as Services, service } from '@ember/service';
import sortBy from 'lodash-es/sortBy';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import BaseController from 'uplisting-frontend/pods/base/controller';

export type ChecklistFilter = 'all' | 'with_checklist' | 'without_checklist';

export interface ChangeoverSettingRow {
  property: ModelRegistry['property'];
  template: ModelRegistry['checklist-template'] | undefined;
  setting: ModelRegistry['changeover-setting'] | undefined;
}

export const CHASE_HOUR_OPTIONS: number[] = [0, 1, 2, 3, 4, 6, 8, 12, 24];

export default class ActionChangeoverSettingsController extends BaseController {
  @service('repositories/checklist-template')
  checklistTemplateRepository!: Services['repositories/checklist-template'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service sentry!: Services['sentry'];

  @tracked query = '';
  @tracked checklistFilter: ChecklistFilter = 'all';

  @tracked editingTemplate: ModelRegistry['checklist-template'] | null = null;
  @tracked openingPropertyId: string | null = null;

  @tracked changeoverSettings!: ModelRegistry['changeover-setting'][];
  @tracked isSavingCreation = false;
  @tracked isSavingNotifications = false;

  bulkChangeoverSetting!: ModelRegistry['bulk-changeover-setting'];

  hourOptions = CHASE_HOUR_OPTIONS;

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get templates(): ModelRegistry['checklist-template'][] {
    return this.checklistTemplateRepository.peekAll();
  }

  @cached
  get rows(): ChangeoverSettingRow[] {
    const { templates, changeoverSettings } = this;

    return sortBy(this.properties, 'nickname').map((property) => ({
      property,
      template: templates.find(
        (template) => template.property?.id === property.id,
      ),
      setting: changeoverSettings.find(
        (record) => record.property?.id === property.id,
      ),
    }));
  }

  get accountConfiguration(): ModelRegistry['account-configuration'] {
    return this.accountConfigurationRepository.currentAccountConfiguration;
  }

  @cached
  get isCreationAutomaticForAll(): boolean {
    const { changeoverSettings } = this;

    return (
      !!changeoverSettings.length &&
      changeoverSettings.every((record) => record.enabled)
    );
  }

  @cached
  get notifyAfterHours(): number {
    return this.#toHours(this.accountConfiguration.taskNotifyAfterMinutes);
  }

  @cached
  get reassignAfterHours(): number {
    return this.#toHours(this.accountConfiguration.taskReassignAfterMinutes);
  }

  @cached
  get filteredRows(): ChangeoverSettingRow[] {
    const needle = this.query.trim().toLowerCase();

    return this.rows.filter((row) => {
      if (
        needle &&
        !(row.property.nickname ?? '').toLowerCase().includes(needle)
      ) {
        return false;
      }

      const hasChecklist = (row.template?.itemsCount ?? 0) > 0;

      if (this.checklistFilter === 'with_checklist') {
        return hasChecklist;
      }

      if (this.checklistFilter === 'without_checklist') {
        return !hasChecklist;
      }

      return true;
    });
  }

  @cached
  get isFiltered(): boolean {
    return this.query.trim().length > 0 || this.checklistFilter !== 'all';
  }

  @cached
  get withChecklistCount(): number {
    return this.rows.filter((row) => (row.template?.itemsCount ?? 0) > 0)
      .length;
  }

  @cached
  get editingProperty(): ModelRegistry['property'] | undefined {
    const template = this.editingTemplate;

    if (!template) {
      return undefined;
    }

    return this.rows.find((row) => row.template === template)?.property;
  }

  @action
  @waitFor
  async handleEditRow(row: ChangeoverSettingRow): Promise<void> {
    const { template } = row;

    if (!template) {
      this.editingTemplate = this.checklistTemplateRepository.createRecord({
        property: row.property,
        name: '',
        items: [],
      });

      return;
    }

    this.openingPropertyId = row.property.id;

    try {
      this.editingTemplate = await this.checklistTemplateRepository.findRecord(
        template.id,
        { reload: true },
      );
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.openingPropertyId = null;
    }
  }

  @action
  isOpening(row: ChangeoverSettingRow): boolean {
    return this.openingPropertyId === row.property.id;
  }

  @action
  handleSearch(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
  }

  @action
  handleSetChecklistFilter(value: ChecklistFilter): void {
    this.checklistFilter = value;
  }

  @action
  handleClosePanel(): void {
    const template = this.editingTemplate;

    this.editingTemplate = null;

    if (template && !template.isPersisted) {
      template.unloadRecord();
    } else {
      template?.rollbackAttributes();
    }
  }

  @action
  @waitFor
  async handleToggleCreation(row: ChangeoverSettingRow): Promise<void> {
    const { setting } = row;

    if (!setting || this.isSavingCreation) {
      return;
    }

    this.isSavingCreation = true;
    setting.enabled = !setting.enabled;

    try {
      await setting.save();

      this.notifications.info();
    } catch (error) {
      setting.rollbackAttributes();
      this.sentry.captureException(error);
      this.notifications.error();
    }

    this.isSavingCreation = false;
  }

  @action
  @waitFor
  async handleChangeCreationForAll(): Promise<void> {
    this.isSavingCreation = true;
    this.bulkChangeoverSetting.enabled = !this.isCreationAutomaticForAll;

    try {
      await this.bulkChangeoverSetting.save();

      this.notifications.info();
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }

    this.isSavingCreation = false;
  }

  @action
  @waitFor
  async handleSelectNotifyAfterHours(hours: number): Promise<void> {
    await this.#saveNotifications({ taskNotifyAfterMinutes: hours });
  }

  @action
  @waitFor
  async handleSelectReassignAfterHours(hours: number): Promise<void> {
    await this.#saveNotifications({ taskReassignAfterMinutes: hours });
  }

  async #saveNotifications(hours: {
    taskNotifyAfterMinutes?: number;
    taskReassignAfterMinutes?: number;
  }): Promise<void> {
    const model = this.accountConfiguration;

    this.isSavingNotifications = true;

    if ('taskNotifyAfterMinutes' in hours) {
      model.taskNotifyAfterMinutes = this.#toMinutes(
        hours.taskNotifyAfterMinutes,
      );
    }

    if ('taskReassignAfterMinutes' in hours) {
      model.taskReassignAfterMinutes = this.#toMinutes(
        hours.taskReassignAfterMinutes,
      );
    }

    try {
      await model.save();

      this.notifications.info();
    } catch (error) {
      model.rollbackAttributes();
      this.sentry.captureException(error);
      this.notifications.error();
    }

    this.isSavingNotifications = false;
  }

  #toHours(minutes: number | null | undefined): number {
    if (!minutes) {
      return 0;
    }

    return Math.max(Math.ceil(minutes / 60), 1);
  }

  #toMinutes(hours: number | undefined): number | null {
    return hours ? hours * 60 : null;
  }
}
